import React from "react";

import styles from "./FillFrameImage.scss";

/**
 * Fill Frame Image Component
 *
 * Renders an image with default styling to crop any user-uploaded image to a 1.25:1
 * aspect ratio, by default, with a prop to adjust the aspect ratio and the object fit
 * based on the caller's needs. Includes an enlarged blurred image so that cropped
 * images have a nice background aesthetic.
 *
 * @param props See {@link FillFrameImageProps}
 */

export const DEFAULT_RATIO = "1.25/1"; // 80% ratio
export const DEFAULT_OBJECT_FIT = "contain";
export const DEFAULT_MAX_HEIGHT = "max-content";

/**
 * Props for {@link FillFrameImage}
 *
 * @property image url string where the image to render is hosted
 * @property alt alternate text for the rendered image
 * @property aspectRatio aspect ratio of the image. Defaults to 1.25/1
 * @property objectFit object fit of the image. Defaults to contain
 * @property maxHeight sets the max height of the image. Defaults to 'max-content'
 * @property className Class name(s) to be applied to component
 * @property size Pixel size of the horizontal padding. (Not used in implementation)
 * @property children Inner text/component (Not used in implementation)
 */
export interface FillFrameImageProps {
  image: string;
  alt: string;
  aspectRatio?: string;
  objectFit?: "fill" | "contain" | "cover" | "none" | "scale-down";
  maxHeight?: string;
  className?: string;
  size?: number;
  children?: React.ReactNode;
}

export const FillFrameImage: React.FC<FillFrameImageProps> = ({
  image,
  alt,
  aspectRatio,
  objectFit,
  maxHeight,
  className,
}) => {
  return (
    <div
      className={styles["image-wrapper"] + (className ? ` ${className}` : "")}
      style={{ maxHeight: maxHeight || DEFAULT_MAX_HEIGHT }}
    >
      <img
        src={image}
        alt={alt}
        className={styles["main-image"]}
        data-testid="img"
        style={{
          aspectRatio: aspectRatio || DEFAULT_RATIO,
          objectFit: objectFit || DEFAULT_OBJECT_FIT,
        }}
      />
      <span className={styles["overlay"]} />
      <span
        data-testid="img-background"
        className={styles["background-image"]}
        style={{
          backgroundImage: `url('${image}')`,
        }}
      />
    </div>
  );
};
